import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaFileExport } from 'react-icons/fa';
import '../styles/Reports.css';
import { useAuth } from '../context/AuthContext';

const SellerReportsPage = () => {
  const [assignedPOS, setAssignedPOS] = useState(null);
  const [reportType, setReportType] = useState('sales');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  const [reportFormat, setReportFormat] = useState('executive');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Establecer fechas por defecto (primer día del mes actual hasta último día del mes actual)
  useEffect(() => {
    const now = new Date();
    // Primer día del mes actual
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    // Último día del mes actual
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  // Cargar punto de venta asignado al vendedor
  useEffect(() => {
    const fetchAssignedPointOfSale = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get('/api/users/assigned-point-of-sale', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data) {
          console.log('Punto de venta asignado:', response.data);
          setAssignedPOS(response.data);
        } else {
          setError('No tienes un punto de venta asignado. Contacta a tu administrador.');
        }
      } catch (error) {
        console.error('Error fetching assigned point of sale:', error);
        setError('No se pudo cargar tu punto de venta asignado. Por favor, inténtalo de nuevo.');
        
        // Si no está autorizado, redirigir al login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedPointOfSale();
  }, [navigate]);

  const handleGenerateReport = async () => {
    if (!assignedPOS || !startDate || !endDate) {
      setError('Por favor, espera a que se cargue tu punto de venta asignado y verifica el rango de fechas.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      let salesData = [];
      let expensesData = [];

      // Obtener datos de ventas si se seleccionó ese tipo de reporte o ambos
      if (reportType === 'sales' || reportType === 'both') {
        const salesResponse = await axios.get(`/api/sales/report`, {
          params: {
            pointOfSaleId: assignedPOS.id,
            startDate,
            endDate
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        salesData = salesResponse.data.map(sale => {
          // Guardar la fecha original para mantener la consistencia con la base de datos
          const originalDate = sale.sale_fecha;
          
          return {
            ...sale,
            type: 'Venta',
            // Guardar la fecha original y la formateada
            date: originalDate,
            formattedDate: formatDate(originalDate),
            amount: sale.total
          };
        });
      }

      // Obtener datos de gastos si se seleccionó ese tipo de reporte o ambos
      if (reportType === 'expenses' || reportType === 'both') {
        const expensesResponse = await axios.get(`/api/expenses/report`, {
          params: {
            pointOfSaleId: assignedPOS.id,
            startDate,
            endDate
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        expensesData = expensesResponse.data.map(expense => ({
          ...expense,
          type: 'Gasto',
          // Guardar la fecha original y la formateada
          date: expense.gasto_fecha,
          formattedDate: formatDate(expense.gasto_fecha),
          amount: expense.amount,
          expense_description: expense.description
        }));
      }

      // Combinar y ordenar los datos por fecha
      const combinedData = [...salesData, ...expensesData].sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });

      setReportData(combinedData);
      setReportGenerated(true);
      setSuccessMessage('Reporte generado exitosamente');
      
      // Ocultar mensaje después de 3 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error generating report:', error);
      
      // Manejar diferentes tipos de errores
      if (error.response) {
        // El servidor respondió con un código de estado diferente de 2xx
        if (error.response.status === 401) {
          navigate('/login');
        } else if (error.response.status === 404) {
          // No se encontraron datos
          setReportData([]);
          setReportGenerated(true);
          setError(null);
        } else {
          setError(error.response.data?.message || 'Error al generar el reporte. Por favor, inténtelo de nuevo.');
        }
      } else if (error.request) {
        // La solicitud se realizó pero no se recibió respuesta
        setError('No se pudo conectar con el servidor. Verifique su conexión a internet.');
      } else {
        // Ocurrió un error al configurar la solicitud
        setError('Error al generar el reporte. Por favor, inténtelo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calcular totales
  const calculateTotals = () => {
    if (!reportData.length) return { salesTotal: 0, expensesTotal: 0, balance: 0 };
    
    const salesTotal = reportData
      .filter(item => item.type === 'Venta')
      .reduce((sum, item) => sum + parseFloat(item.amount), 0);
    
    const expensesTotal = reportData
      .filter(item => item.type === 'Gasto')
      .reduce((sum, item) => sum + parseFloat(item.amount), 0);
    
    return {
      salesTotal,
      expensesTotal,
      balance: salesTotal - expensesTotal
    };
  };

  const totals = calculateTotals();

  // Función para formatear la fecha en formato legible sin manipulación de zona horaria
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Fecha no disponible';
      
      // Si la fecha ya viene en formato YYYY-MM-DD o con formato ISO
      if (typeof dateString === 'string') {
        // Extraer solo la parte de la fecha (antes de la T si existe)
        const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
        
        // Convertir formato de fecha YYYY-MM-DD a DD/MM/YYYY
        const parts = datePart.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`; // DD/MM/YYYY
        }
        
        // Si no se pudo dividir por '-', devolver la fecha original
        return datePart;
      }
      
      // Si es un objeto Date o un timestamp, extraer los componentes manualmente
      // para evitar problemas de zona horaria
      const dateObj = new Date(dateString);
      if (isNaN(dateObj.getTime())) return 'Fecha inválida';
      
      // Obtener año, mes y día en UTC para evitar ajustes de zona horaria
      const day = String(dateObj.getUTCDate()).padStart(2, '0');
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const year = dateObj.getUTCFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return dateString || 'Fecha no disponible';
    }
  };

  // Función para exportar datos a CSV
  const exportToCSV = (reportType) => {
    if (!reportData || reportData.length === 0) {
      alert('No hay datos para exportar');
      return;
    }
    
    // Definir encabezados según el tipo de reporte
    let headers = ['Fecha', 'Tipo', 'Punto de Venta'];
    
    if (reportType === 'detailed') {
      headers.push('Descripción');
    }
    
    headers = headers.concat(['Monto', 'Método de Pago']);
    
    // Crear filas de datos
    const rows = reportData.map(item => {
      const row = [
        formatDate(item.date),
        item.type,
        assignedPOS?.pos_name || 'Punto de venta'
      ];
      
      if (reportType === 'detailed') {
        // Agregar descripción para reporte detallado
        if (item.type === 'Venta') {
          if (item.sales_detail && item.sales_detail.length > 0) {
            const description = item.sales_detail.map(detail => {
              const productName = detail.product_name || 
                               detail.products?.prod_name || 
                               'Producto';
              const quantity = detail.sd_quantity || 
                            detail.quantity || 
                            1;
              return `${productName} (${quantity})`;
            }).join(', ');
            // Siempre agregar comillas a la descripción
            row.push(`"${description}"`);
          } else {
            row.push('"Sin detalles"');
          }
        } else {
          const expenseDesc = item.expense_description || 'Sin descripción';
          row.push(`"${expenseDesc}"`);
        }
      }
      
      row.push(parseFloat(item.amount).toFixed(2));
      row.push(item.paymentMethod);
      
      return row;
    });
    
    // Combinar encabezados y filas
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Crear blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_${reportType === 'executive' ? 'ejecutivo' : 'detallado'}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !reportGenerated) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Reportes</h1>
        <button onClick={() => navigate('/seller-dashboard')} className="back-btn">
          Volver al Dashboard
        </button>
      </header>

      <div className="reports-section">
        <div className="reports-section-header">
          <h2>Generar Reporte</h2>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="report-filters">
          <div className="filter-group">
            <label htmlFor="pointOfSale">Punto de Venta:</label>
            <input 
              type="text" 
              id="pointOfSale" 
              value={assignedPOS?.pos_name || 'Cargando...'}
              disabled={true}
              className="disabled-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="reportType">Tipo de Reporte:</label>
            <select 
              id="reportType" 
              value={reportType} 
              onChange={(e) => setReportType(e.target.value)}
              disabled={loading}
            >
              <option value="sales">Ventas</option>
              <option value="expenses">Gastos</option>
              <option value="both">Ambos</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Rango de Fechas:</label>
            <div className="date-filters">
              <div className="date-group">
                <label htmlFor="startDate">Desde:</label>
                <input 
                  type="date" 
                  id="startDate" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading}
                  className="date-input"
                />
              </div>

              <div className="date-group">
                <label htmlFor="endDate">Hasta:</label>
                <input 
                  type="date" 
                  id="endDate" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={loading}
                  className="date-input"
                />
              </div>
            </div>
          </div>
          
          <div className="filter-group">
            <label htmlFor="reportFormat">Formato de Reporte:</label>
            <select 
              id="reportFormat" 
              value={reportFormat} 
              onChange={(e) => setReportFormat(e.target.value)}
              disabled={loading}
            >
              <option value="executive">Reporte Ejecutivo</option>
              <option value="detailed">Reporte Detallado</option>
            </select>
          </div>

          <button 
            className="generate-report-btn"
            onClick={handleGenerateReport}
            disabled={loading || !assignedPOS || !startDate || !endDate}
          >
            {loading ? 'Generando...' : 'Generar Reporte'}
          </button>
        </div>

        {reportGenerated && (
          <div className="report-results">
            {reportData.length === 0 ? (
              <div className="no-data">
                <p>No hay datos disponibles para el período seleccionado.</p>
                <p>Pruebe con un rango de fechas diferente.</p>
                <p>Nota: Si seleccionó "Gastos" o "Ambos", verifique que existan gastos registrados en el período seleccionado.</p>
              </div>
            ) : (
              <div className="report-container">
                <div className="report-header">
                  <h3 className="section-title">{reportFormat === 'executive' ? 'Reporte Ejecutivo' : 'Reporte Detallado'}</h3>
                  <button 
                    className="export-csv-btn" 
                    onClick={() => exportToCSV(reportFormat)}
                    title="Exportar a CSV">
                    <FaFileExport /> Exportar CSV
                  </button>
                </div>
                <div className="report-summary">
                  <div className="summary-item">
                    <span className="summary-label">Periodo:</span>
                    <span>{startDate} - {endDate}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Punto de Venta:</span>
                    <span>{assignedPOS?.pos_name}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Ventas:</span>
                    <span className="summary-value sales">${totals.salesTotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Gastos:</span>
                    <span className="summary-value expenses">${totals.expensesTotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Balance:</span>
                    <span className={`summary-value balance ${totals.balance >= 0 ? 'positive' : 'negative'}`}>
                      ${totals.balance.toFixed(2)}
                    </span>
                  </div>
                </div>

                {reportFormat === 'executive' ? (
                  // Reporte Ejecutivo
                  <div className="report-data">
                    <table className="reports-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Tipo</th>
                          <th>Descripción</th>
                          <th>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((item, index) => (
                          <tr key={index} className={item.type === 'Venta' ? 'sale-row' : 'expense-row'}>
                            <td>{item.formattedDate}</td>
                            <td>
                              <span className={`item-type ${item.type === 'Venta' ? 'sale' : 'expense'}`}>
                                {item.type}
                              </span>
                            </td>
                            <td>
                              {item.type === 'Venta' 
                                ? `Venta en ${assignedPOS?.pos_name || 'Punto de venta'} - ${item.sale_tipopago ? (item.sale_tipopago.charAt(0).toUpperCase() + item.sale_tipopago.slice(1)) : 'Efectivo'}` 
                                : `Gasto en ${assignedPOS?.pos_name || 'Punto de venta'} - ${item.gasto_tipopago ? (item.gasto_tipopago.charAt(0).toUpperCase() + item.gasto_tipopago.slice(1)) : 'Efectivo'}`}
                            </td>
                            <td className="amount">${parseFloat(item.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  // Reporte Detallado
                  <div className="report-data">
                    <table className="reports-table detailed">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Tipo</th>
                          <th>Método de Pago</th>
                          <th>Detalles</th>
                          <th>Descripción</th>
                          <th>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((item, index) => (
                          <tr key={index} className={item.type === 'Venta' ? 'sale-row' : 'expense-row'}>
                            <td>{item.formattedDate}</td>
                            <td>
                              <span className={`item-type ${item.type === 'Venta' ? 'sale' : 'expense'}`}>
                                {item.type}
                              </span>
                            </td>
                            <td>
                              {item.type === 'Venta' 
                                ? (item.sale_tipopago ? (item.sale_tipopago.charAt(0).toUpperCase() + item.sale_tipopago.slice(1)) : 'Efectivo')
                                : (item.gasto_tipopago ? (item.gasto_tipopago.charAt(0).toUpperCase() + item.gasto_tipopago.slice(1)) : 'Efectivo')}
                            </td>
                            <td>
                              {item.type === 'Venta' 
                                ? 'Venta registrada'
                                : (item.expense_type ? item.expense_type.name : 'Gasto general')}
                            </td>
                            <td>
                              {item.type === 'Venta' 
                                ? (item.sales_detail && item.sales_detail.length > 0 
                                    ? item.sales_detail.map(detail => {
                                        const productName = detail.product_name || 
                                                         detail.products?.prod_name || 
                                                         'Producto';
                                        const quantity = detail.sd_quantity || 
                                                      detail.quantity || 
                                                      1;
                                        return `${productName} (${quantity})`;
                                      }).join(', ')
                                    : 'Sin detalles')
                                : (item.expense_description || 'Sin descripción')}
                            </td>
                            <td className="amount">${parseFloat(item.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerReportsPage;
